// Comment-only lines: text-only quote lines (no product, qty, UOM, price or availability) that keep
// their position, print on customer documents and go to the ERP in position. Always customer-facing.
// Render off `is_comment_line` only — never infer from an empty product or zero quantity.

// Per-tenant UI rule (Quoteheader.Features) that shows the "Add comment line" option. Existing
// comment lines always render/edit/reorder/delete regardless; the backend also enforces the
// per-customer allow_comment_lines switch (400 when off).
export const COMMENT_LINES_FEATURE = 'CommentLines'

export const COMMENT_MAX_LENGTH = 250

export const isCommentLinesEnabled = (uirules) => uirules?.Quoteheader?.Features?.includes(COMMENT_LINES_FEATURE) || false

export const isCommentLine = (line) => line?.is_comment_line === true

// Lines that count: totals, quantity, margin and line counts all ignore comments (backend too).
export const productLinesOnly = (lines) => (Array.isArray(lines) ? lines.filter((line) => !isCommentLine(line)) : [])

// Non-deleted product lines — the client-side "items" count.
export const countProductLines = (lines) => productLinesOnly(lines).filter((line) => !line?.deleted).length

// "Change to product" inserts the product at the comment's line and soft-deletes the comment with this
// feedback (a line's type can't change). The product takes over that line, so the replaced comment is
// dropped from the quote instead of lingering as an extra struck-through line (or reappearing on reload).
export const REPLACED_WITH_PRODUCT_FEEDBACK = 'Replaced with product'

export const isReplacedCommentLine = (line) =>
  isCommentLine(line) && Boolean(line?.deleted) && line?.feedback === REPLACED_WITH_PRODUCT_FEEDBACK

export const withoutReplacedCommentLines = (orderDetails) => {
  const lines = orderDetails?.line_items
  if (!Array.isArray(lines) || !lines.some(isReplacedCommentLine)) return orderDetails
  return { ...orderDetails, line_items: lines.filter((line) => !isReplacedCommentLine(line)) }
}

// Bulk product actions with explicit ids=... 400 when a comment id is included, so strip them.
export const withoutCommentLineIds = (ids, lines) => {
  const commentIds = new Set((lines || []).filter(isCommentLine).map((line) => line.id))
  return (ids || []).filter((id) => !commentIds.has(id))
}

// Mirrors the backend's validation (it trims, then requires 1–250 chars); '' when valid.
export const getCommentTextError = (text) => {
  const trimmed = String(text ?? '').trim()
  if (!trimmed) return 'Comment text is required.'
  if (trimmed.length > COMMENT_MAX_LENGTH) return `Comment must be at most ${COMMENT_MAX_LENGTH} characters.`
  return ''
}

// ERP status hint for a comment on an order already in the ERP:
// - 'sent': submitted with the order (erp_line_id set); edits stay in Revalgo only.
// - 'not_in_erp': added after submit (erp_line_id null); never sent on update.
// null when the quote isn't in the ERP yet.
export const getCommentErpStatus = (line, orderDetails) => {
  if (!isCommentLine(line) || !orderDetails?.erp_order_no) return null
  return line.erp_line_id != null ? 'sent' : 'not_in_erp'
}

// The API's specific reason ("comment_text must be at most 250 characters", "Comment lines are not
// enabled for this customer.") is in internal_message; message is often just the status text
// ("Bad Request"), so it's only a fallback.
export const getCommentApiError = (error, fallback) => {
  const data = error?.response?.data
  return data?.internal_message || data?.error?.message || (typeof data?.error === 'string' && data.error) || data?.message || fallback
}
